import { useEffect, useState } from 'react';
import type { Entitlement } from '@compflash/contracts';
import { useSession } from '../lib/session';
import { formatDateTime, formatMoney } from '../lib/format';

interface PlanSummary {
  id: string;
  name: string;
  description: string;
  monthlyPriceMinor: number;
  annualPriceMinor: number | null;
  currency: string;
  features: Entitlement['features'];
  contactOnly?: boolean;
}

export function BillingPage(): JSX.Element {
  const { client, entitlement, usage } = useSession();
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void client.plans().then((response) => setPlans(response.plans));
  }, [client]);

  const startCheckout = async (planId: string): Promise<void> => {
    setMessage(null);
    try {
      const { url } = await client.startCheckout(planId, 'monthly');
      window.location.href = url;
    } catch (caught) {
      setMessage((caught as Error).message);
    }
  };

  return (
    <div className="stack">
      <section className="panel">
        <h2>Your plan</h2>
        {entitlement ? (
          <div className="stat-grid">
            <div className="stat"><span className="label">Plan</span><strong>{entitlement.planName}</strong></div>
            <div className="stat"><span className="label">Status</span><strong>{entitlement.status.replace(/_/g, ' ')}</strong></div>
            <div className="stat"><span className="label">Source</span><strong>{entitlement.source.replace(/_/g, ' ')}</strong></div>
            <div className="stat"><span className="label">Renews</span><strong>{formatDateTime(entitlement.currentPeriodEnd)}</strong></div>
          </div>
        ) : (
          <p className="muted">Loading…</p>
        )}
        {usage && (
          <>
            <p className="muted small">
              {usage.scansUsedToday} scans today
              {usage.scansPerDay !== null ? ` of ${usage.scansPerDay}` : ''} · {usage.scansUsed} this month
              {usage.scansIncluded !== null ? ` of ${usage.scansIncluded}` : ''}
              {usage.overageScans > 0 ? ` · ${usage.overageScans} in overage` : ''}
            </p>
            {usage.approachingDailyLimit && (
              <p className="warn small">You are approaching today's scan limit.</p>
            )}
            {usage.dailyGracePeriod && (
              <p className="warn small">
                You have passed today's scan limit but are in the grace buffer.
                {usage.dailyGraceRemaining !== null ? ` ${usage.dailyGraceRemaining} grace scans remaining.` : ''}
              </p>
            )}
          </>
        )}
      </section>

      <section className="cards">
        {plans.map((plan) => (
          <article className={`panel plan ${entitlement?.planId === plan.id ? 'plan-current' : ''}`} key={plan.id}>
            <h3>{plan.name}</h3>
            <p className="price">
              {plan.contactOnly
                ? 'Contact us'
                : plan.monthlyPriceMinor === 0
                  ? 'Free'
                  : `${formatMoney({ amountMinor: plan.monthlyPriceMinor, currency: plan.currency })}/mo`}
            </p>
            {!plan.contactOnly && plan.annualPriceMinor !== null && (
              <p className="muted small">
                or {formatMoney({ amountMinor: plan.annualPriceMinor, currency: plan.currency })}/year
              </p>
            )}
            <p className="muted">{plan.description}</p>
            <ul className="muted small">
              <li>{plan.features.scansPerDay !== null ? `${plan.features.scansPerDay} scans a day` : 'Custom scan volume'}</li>
              <li>{plan.features.liveOverlay ? 'Live overlay + automatic capture' : 'Manual capture'}</li>
              <li>{plan.features.compsHistoryDays} days of comp history</li>
              <li>{plan.features.csvExport ? 'CSV export' : 'No export'}</li>
              {plan.features.seats > 1 && <li>{plan.features.seats} seats</li>}
              {!plan.contactOnly && plan.monthlyPriceMinor > 0 && <li>No per-scan fees</li>}
            </ul>
            {plan.contactOnly && entitlement?.planId !== plan.id && (
              <a href="mailto:hello@compflash.com" className="primary button">Contact sales</a>
            )}
            {!plan.contactOnly && plan.monthlyPriceMinor > 0 && entitlement?.planId !== plan.id && (
              <button type="button" className="primary" onClick={() => void startCheckout(plan.id)}>
                Choose {plan.name}
              </button>
            )}
            {entitlement?.planId === plan.id && <p className="muted small">Current plan</p>}
          </article>
        ))}
      </section>

      {message && <p className="error">{message}</p>}

      <section className="panel">
        <h3>How billing works across platforms</h3>
        <ul className="muted small">
          <li>Web subscriptions are billed here. The Chrome extension cannot take payments because Chrome Web Store Payments is retired, so it uses the entitlement from your account.</li>
          <li>The iOS app sells the same plans through StoreKit in-app purchase, which the App Store requires for digital subscriptions.</li>
          <li>If you end up subscribed on two platforms, the higher plan applies and support will refund the duplicate.</li>
        </ul>
      </section>
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { client, user, refreshAccount } = useSession();
  const [riskProfile, setRiskProfile] = useState(user?.preferences.riskProfile ?? 'balanced');
  const [intent, setIntent] = useState(user?.preferences.intent ?? 'collect');
  const [sellFeeRate, setSellFeeRate] = useState(String(user?.preferences.sellFeeRate ?? 0.1325));
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setRiskProfile(user.preferences.riskProfile);
    setIntent(user.preferences.intent);
    setSellFeeRate(String(user.preferences.sellFeeRate));
  }, [user]);

  const save = async (): Promise<void> => {
    await client.updateProfile({
      preferences: { riskProfile, intent, sellFeeRate: Number(sellFeeRate) || 0 },
    });
    await refreshAccount();
    setStatus('Saved.');
    setTimeout(() => setStatus(null), 1800);
  };

  const exportData = async (): Promise<void> => {
    const data = await client.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'compflash-export.json';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const deleteAccount = async (): Promise<void> => {
    if (!window.confirm('Delete your account and all scans? This cannot be undone.')) return;
    await client.deleteAccount();
    window.location.href = '/';
  };

  return (
    <div className="stack">
      <section className="panel">
        <h2>Bidding preferences</h2>
        <p className="muted small">These defaults are applied to every scan and lookup, on every device.</p>
        <div className="field-grid">
          <label className="field">
            <span>Risk profile</span>
            <select value={riskProfile} onChange={(event) => setRiskProfile(event.target.value as typeof riskProfile)}>
              <option value="conservative">Conservative — require a large discount</option>
              <option value="balanced">Balanced</option>
              <option value="aggressive">Aggressive — bid close to fair value</option>
            </select>
          </label>
          <label className="field">
            <span>Intent</span>
            <select value={intent} onChange={(event) => setIntent(event.target.value as typeof intent)}>
              <option value="collect">Collecting</option>
              <option value="flip">Flipping (subtract resale fees)</option>
            </select>
          </label>
          <label className="field">
            <span>Resale fee rate</span>
            <input value={sellFeeRate} onChange={(event) => setSellFeeRate(event.target.value)} inputMode="decimal" />
            <span className="hint">Used only when your intent is flipping. 0.1325 is a typical marketplace rate.</span>
          </label>
        </div>
        <button type="button" className="primary" onClick={() => void save()}>Save preferences</button>
        {status && <span className="ok"> {status}</span>}
      </section>

      <section className="panel">
        <h2>Privacy and data</h2>
        <ul className="muted small">
          <li>Card images are processed in memory and are not stored unless image retention is explicitly enabled by the operator.</li>
          <li>Scan results, corrections and watchlists are kept against your account until you delete them.</li>
          <li>You can export everything we hold, or erase your account entirely.</li>
        </ul>
        <div className="row">
          <button type="button" className="ghost" onClick={() => void exportData()}>Export my data</button>
          <button type="button" className="danger" onClick={() => void deleteAccount()}>Delete my account</button>
        </div>
      </section>
    </div>
  );
}

export function StatusPage(): JSX.Element {
  const { client } = useSession();
  const [providers, setProviders] = useState<Array<{ name: string; kind: string; status: string; detail: string | null; p50LatencyMs: number | null; p95LatencyMs: number | null; errorRate: number | null }>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void client
      .status()
      .then((response) => setProviders(response.providers))
      .catch((caught: unknown) => setError((caught as Error).message));
  }, [client]);

  return (
    <section className="panel">
      <h2>Provider status</h2>
      <p className="muted small">
        Identification and market-data providers are external dependencies. When one degrades, scans fall back to cached
        comps and the result says so rather than quietly getting worse.
      </p>
      {error && <p className="error">{error}</p>}
      <table className="table">
        <thead>
          <tr><th>Provider</th><th>Type</th><th>Status</th><th className="num">p50</th><th className="num">p95</th><th className="num">Errors</th><th>Detail</th></tr>
        </thead>
        <tbody>
          {providers.map((provider) => (
            <tr key={provider.name}>
              <td>{provider.name}</td>
              <td>{provider.kind.replace(/_/g, ' ')}</td>
              <td className={provider.status === 'healthy' ? 'ok' : 'warn'}>{provider.status}</td>
              <td className="num">{provider.p50LatencyMs !== null ? `${Math.round(provider.p50LatencyMs)} ms` : '—'}</td>
              <td className="num">{provider.p95LatencyMs !== null ? `${Math.round(provider.p95LatencyMs)} ms` : '—'}</td>
              <td className="num">{provider.errorRate !== null ? `${Math.round(provider.errorRate * 100)}%` : '—'}</td>
              <td className="muted small">{provider.detail ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
